import type { Disclosure } from '@/components/common/helpers';
import type { EmailLoginVerifyResponse } from '@/hooks/auth';
import { useEmailAuth } from '@/hooks/auth';
import { isValidEmail } from '@/lib/email';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Form,
  Alert,
} from '@heroui/react';
import type { KeyboardEvent } from 'react';
import { useEffect, useState } from 'react';

export function EmailLogin({ disclosure }: { disclosure: Disclosure }) {
  type Step = 'initiate' | 'verify';
  const [step, setStep] = useState<Step>('initiate');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [initiating, setInitiating] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [alertText, setAlertText] = useState('');
  const { initiateLogin, verifyLogin } = useEmailAuth();
  const initiate = async () => {
    setInitiating(true);
    await initiateLogin(email);
    setInitiating(false);
    setStep('verify');
  };

  const verify = async () => {
    debugger;
    setVerifying(true);
    const res = await verifyLogin(email, code);
    if (res.status !== 'confirmed') {
      handleVerificationStatus(res, setAlertText);
    } else {
      setVerified(true);
    }

    setVerifying(false);
  };

  const handlers: Record<Step, () => void | Promise<void>> = {
    initiate,
    verify,
  };
  const next = handlers[step];
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      next();

      return;
    }
  };

  const validEmail = isValidEmail(email);
  const nextDisabled = verified || !validEmail;
  const isLoading = (() => {
    if (step === 'initiate') {
      return initiating;
    } else if (step === 'verify') {
      return verifying;
    } else {
      return false;
    }
  })();
  useEffect(() => {
    if (disclosure.isOpen) {
      return;
    }

    setStep('initiate');
    setEmail('');
    setCode('');
    setVerifying(false);
    setVerified(false);
    setAlertText('');
  }, [disclosure.isOpen]);
  const randomName = `code-${String(Math.floor(100000000 * Math.random())).padStart(8, '0')}`;
  const validateEmail = () => {
    if (!isValidEmail(email)) {
      setAlertText('Please enter a valid email address.');
    }
  };

  const updateEmail = (value: string) => {
    setEmail(value);
    setAlertText('');
  };

  const updateCode = (value: string) => {
    setCode(value);
    setAlertText('');
  };

  return (
    <Modal
      isOpen={disclosure.isOpen}
      onOpenChange={disclosure.onOpenChange}
      backdrop='opaque'
      placement='center'
      scrollBehavior='inside'
      size='md'
    >
      <ModalContent>
        <>
          <ModalHeader className='flex flex-col gap-1'>Email Login</ModalHeader>
          <ModalBody>
            {step === 'initiate' && (
              <div className='p-4'>
                <p>Enter your email to continue.</p>
                <p>We&apos;ll send you a code you can use to log in.</p>
                <Form autoComplete='on'>
                  <Input
                    className='mt-4'
                    type='email'
                    placeholder='Your email address...'
                    autoComplete='email'
                    autoCapitalize='off'
                    autoFocus
                    autoCorrect='off'
                    onKeyDown={onKeyDown}
                    value={email}
                    onValueChange={updateEmail}
                    onBlur={validateEmail}
                  />
                </Form>
              </div>
            )}
            {step === 'verify' && (
              <>
                <p>Enter the code you received.</p>
                <Input
                  className='mw-20 mt-4 font-mono'
                  autoComplete='new-password'
                  name={randomName}
                  value={code}
                  onValueChange={updateCode}
                  onKeyDown={onKeyDown}
                  autoFocus
                />
              </>
            )}
            <div className='min-h-16'>
              {alertText && <Alert color='danger' title={alertText} />}
            </div>
          </ModalBody>
          <ModalFooter>
            {step === 'verify' && (
              <Button
                color='danger'
                variant='light'
                onPress={initiate}
                isLoading={initiating}
                isDisabled={verifying}
              >
                Resend
              </Button>
            )}
            <Button
              color='primary'
              onPress={next}
              isLoading={isLoading}
              isDisabled={nextDisabled}
            >
              Next
            </Button>
          </ModalFooter>
        </>
      </ModalContent>
    </Modal>
  );
}

function handleVerificationStatus(
  res: EmailLoginVerifyResponse,
  setAlertText: (value: string) => void,
) {
  switch (res.status) {
    case 'expired':
      setAlertText(
        'The verification code has expired. Please request a new code.',
      );
      break;
    case 'invalid':
      setAlertText(
        'The verification code is invalid. Please check the code and try again.',
      );
      break;
    // case 'failed':
    //   setAlertText('An error occurred during verification. Please try again in a few moments.');
    //   break;
    default:
      setAlertText('Verification failed. Please try again.');
  }
}
